import React, { useState, useEffect } from 'react';
import { FaPlus, FaEdit, FaTrash, FaMoneyBillWave, FaCalendar } from 'react-icons/fa';
import { receitasAPI } from '../../services/api';
import { toast } from 'react-toastify';
import { useAuth } from '../../contexts/AuthContext';
import ReceitaModal from './ReceitaModal';
import './Receitas.css';

const ReceitasList = () => {
  const [receitas, setReceitas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingReceita, setEditingReceita] = useState(null);
  const [dateFilter, setDateFilter] = useState({
    startDate: new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0],
    endDate: new Date().toISOString().split('T')[0]
  });
  const [totalReceitas, setTotalReceitas] = useState(0);
  const { hasPermission } = useAuth();

  useEffect(() => {
    loadReceitas();
  }, [dateFilter]);

  const loadReceitas = async () => {
    try {
      setLoading(true);
      const { data } = await receitasAPI.getAll(dateFilter);
      setReceitas(data.data);
      
      // Get total
      const totalRes = await receitasAPI.getTotal(dateFilter);
      setTotalReceitas(totalRes.data.data.total);
    } catch (error) {
      toast.error('Erro ao carregar receitas');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id, nome) => {
    if (!window.confirm(`Deseja realmente excluir a receita "${nome}"?`)) {
      return;
    }

    try {
      await receitasAPI.delete(id);
      toast.success('Receita excluída com sucesso!');
      loadReceitas();
    } catch (error) {
      toast.error('Erro ao excluir receita');
      console.error(error);
    }
  };

  const handleEdit = (receita) => {
    setEditingReceita(receita);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingReceita(null);
  };

  const handleSaveSuccess = () => {
    loadReceitas();
    handleCloseModal();
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('pt-BR');
  };

  if (loading) {
    return (
      <div className="receitas-container">
        <div className="loading">
          <div className="spinner"></div>
          <p>Carregando receitas...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="receitas-container">
      <div className="page-header">
        <div>
          <h1><FaMoneyBillWave /> Gestão de Receitas</h1>
          <p>Controle de receitas e entradas</p>
        </div>
        {hasPermission('pode_inserir') && (
          <button onClick={() => setShowModal(true)} className="btn btn-primary">
            <FaPlus /> Nova Receita
          </button>
        )}
      </div>

      <div className="stats-bar">
        <div className="stat-item">
          <span className="stat-label">Total do Período</span>
          <span className="stat-value positive">{formatCurrency(totalReceitas)}</span>
        </div>
        <div className="stat-item">
          <span className="stat-label">Quantidade</span>
          <span className="stat-value">{receitas.length} lançamento(s)</span>
        </div>
      </div>

      <div className="filters-card">
        <div className="filter-group">
          <FaCalendar />
          <label>Período:</label>
          <input
            type="date"
            value={dateFilter.startDate}
            onChange={(e) => setDateFilter({ ...dateFilter, startDate: e.target.value })}
          />
          <span>até</span>
          <input
            type="date"
            value={dateFilter.endDate}
            onChange={(e) => setDateFilter({ ...dateFilter, endDate: e.target.value })}
          />
        </div>
      </div>

      <div className="table-card">
        <table className="table">
          <thead>
            <tr>
              <th>Data</th>
              <th>Nome</th>
              <th>Histórico</th>
              <th>Valor</th>
              <th>Ações</th>
            </tr>
          </thead>
          <tbody>
            {receitas.length === 0 ? (
              <tr>
                <td colSpan="5" className="text-center">
                  Nenhuma receita encontrada no período
                </td>
              </tr>
            ) : (
              receitas.map((receita) => (
                <tr key={receita.id}>
                  <td>{formatDate(receita.data_receita)}</td>
                  <td><strong>{receita.nome}</strong></td>
                  <td>{receita.historico || '-'}</td>
                  <td>
                    <span className="value-positive">{formatCurrency(receita.valor)}</span>
                  </td>
                  <td>
                    <div className="action-buttons">
                      {hasPermission('pode_alterar') && (
                        <button
                          onClick={() => handleEdit(receita)}
                          className="btn-icon btn-warning"
                          title="Editar"
                        >
                          <FaEdit />
                        </button>
                      )}
                      {hasPermission('pode_deletar') && (
                        <button
                          onClick={() => handleDelete(receita.id, receita.nome)}
                          className="btn-icon btn-danger"
                          title="Excluir"
                        >
                          <FaTrash />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
          <tfoot>
            <tr className="total-row">
              <td colSpan="3"><strong>Total</strong></td>
              <td><strong className="value-positive">{formatCurrency(totalReceitas)}</strong></td>
              <td></td>
            </tr>
          </tfoot>
        </table>
      </div>

      {showModal && (
        <ReceitaModal
          receita={editingReceita}
          onClose={handleCloseModal}
          onSave={handleSaveSuccess}
        />
      )}
    </div>
  );
};

export default ReceitasList;
